import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useError } from '../../contexts/ErrorContext';

interface LoginFormInputs {
  emailOrUsername: string;
  password: string;
}

const Login: React.FC = () => {
  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormInputs>();
  const { setError } = useError();
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
    try {
      await login(data.emailOrUsername, data.password);
      toast.success('Login successful!');
      navigate('/'); // Redirect to home page
    } catch (error) {
      setError(error as Error);
      // 429: too many failed tries from this address (the backend's sign-in limit).
      if ((error as any)?.response?.status === 429) {
        toast.error('Too many tries. Please wait a few minutes and try again.');
      } else {
        toast.error('Login failed. Please check your credentials and try again.');
      }
    }
  };

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Login</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-3">
          <input
            type="text"
            {...register("emailOrUsername", {
              required: "Email or username is required"
            })}
            className={`form-control ${errors.emailOrUsername ? 'is-invalid' : ''}`}
            placeholder="Email or Username"
            autoCapitalize="none"
            autoCorrect="off"
          />
          {errors.emailOrUsername && <div className="invalid-feedback">{errors.emailOrUsername.message}</div>}
        </div>
        <div className="mb-3">
          <input 
            type="password" 
            {...register("password", { required: "Password is required" })}
            className={`form-control ${errors.password ? 'is-invalid' : ''}`}
            placeholder="Password" 
          />
          {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
        </div>
        <button type="submit" className="btn btn-primary">Login</button>
      </form>
      <div className="mt-3">
        <p>Don't have an account? <Link to="/signup">Sign up here</Link></p>
        <p><Link to="/forgot-password">Forgot your password?</Link></p>
      </div>
    </div>
  );
};

export default Login;